import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import KpiCard from '../components/KpiCard';
import StatusBadge from '../components/StatusBadge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Activity,
  AlertTriangle,
  ShieldAlert,
  CalendarClock,
  Wrench,
  Gauge,
  DollarSign,
  TrendingUp,
  Filter,
  Wind,
  Droplet,
  Thermometer,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';
import { countByBand, mrr } from '../data/mockData';

export default function Dashboard() {
  const { devices, alerts, tickets, customers, plans, technicians } = useApp();
  const navigate = useNavigate();

  const bands = useMemo(() => countByBand(devices), [devices]);
  const totalDevices = devices.length;
  const activeAlerts = alerts.filter((a) => a.status !== 'resolved' && a.status !== 'dismissed').length;
  const critical = alerts.filter((a) => a.severity === 'critical').length;
  const openTickets = tickets.filter((t) => !['completed', 'cancelled'].includes(t.status)).length;
  const avgHealth = Math.round(devices.reduce((s, d) => s + d.healthScore, 0) / devices.length);
  const monthlyMRR = mrr(customers, plans);
  const upcomingMaint = customers.filter((c) => {
    const d = new Date(c.nextScheduledService);
    return (d - new Date()) / 864e5 < 30;
  }).length;
  const prevented = 34;

  const healthTrend = useMemo(() => {
    return Array.from({ length: 12 }).map((_, i) => ({
      d: `W${i + 1}`,
      score: 78 + Math.round(Math.sin(i / 1.5) * 4 + (i > 8 ? 3 : 0)),
      alerts: 14 - Math.round(Math.sin(i / 1.5) * 4),
    }));
  }, []);

  const techWorkload = technicians.map((t) => ({
    name: t.name.split(' ')[0],
    jobs: t.activeTickets,
    full: t.name,
  }));

  const recentAlerts = alerts.slice(0, 6);

  const revenueOpps = [
    { label: 'Filter replacement', count: 7, revenue: 490, icon: Wind, accent: 'blue' },
    { label: 'AC tune-up', count: 12, revenue: 2580, icon: Thermometer, accent: 'emerald' },
    { label: 'Humidity inspection', count: 4, revenue: 1120, icon: Droplet, accent: 'indigo' },
    { label: 'IAQ upgrade', count: 6, revenue: 7200, icon: Sparkles, accent: 'violet' },
    { label: 'Plan renewal', count: 9, revenue: 3150, icon: ShieldAlert, accent: 'amber' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Executive Overview</div>
          <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">Good morning, Jordan</h1>
          <p className="mt-1 text-sm text-slate-500">Here’s what’s happening across your monitored systems today.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9" data-testid="dashboard-filter-btn">
            <Filter className="h-4 w-4 mr-2" /> This Week
          </Button>
          <Button className="h-9 bg-[#0052CC] hover:bg-[#0043A6]" onClick={() => navigate('/alerts')} data-testid="dashboard-view-alerts">
            View Alerts <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard testId="kpi-devices" label="Connected Devices" value={totalDevices.toLocaleString()} icon={Activity} accent="blue" trend={2.4} trendLabel="vs last week" />
        <KpiCard testId="kpi-alerts" label="Active Alerts" value={activeAlerts} icon={AlertTriangle} accent="amber" trend={-8} trendLabel="vs last week" />
        <KpiCard testId="kpi-critical" label="Critical Systems" value={critical} icon={ShieldAlert} accent="red" trend={12} trendLabel="vs last week" />
        <KpiCard testId="kpi-maint" label="Upcoming Maintenance" value={upcomingMaint} icon={CalendarClock} accent="indigo" trendLabel="next 30 days" />
        <KpiCard testId="kpi-tickets" label="Open Service Tickets" value={openTickets} icon={Wrench} accent="slate" trend={-3} trendLabel="vs last week" />
        <KpiCard testId="kpi-health" label="Avg. System Health" value={`${avgHealth}/100`} icon={Gauge} accent="emerald" trend={1.6} trendLabel="vs last week" />
        <KpiCard testId="kpi-mrr" label="Monthly Recurring" value={`$${monthlyMRR.toLocaleString()}`} icon={DollarSign} accent="emerald" trend={4.1} trendLabel="MRR" />
        <KpiCard testId="kpi-prevented" label="Breakdowns Prevented" value={prevented} icon={TrendingUp} accent="violet" trendLabel="YTD estimate" />
      </div>

      {/* Health overview + chart */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 p-6 border-slate-200 shadow-sm" data-testid="health-chart-card">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Fleet Health</div>
              <div className="mt-0.5 text-lg font-semibold text-slate-900">Average health score · last 12 weeks</div>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full bg-[#0052CC]"/>Health</div>
              <div className="flex items-center gap-1.5 text-slate-600"><span className="h-2 w-2 rounded-full bg-amber-400"/>Alerts</div>
            </div>
          </div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={healthTrend}>
                <defs>
                  <linearGradient id="healthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0052CC" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#0052CC" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="d" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} domain={[60, 100]} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Area type="monotone" dataKey="score" stroke="#0052CC" strokeWidth={2} fill="url(#healthGrad)" />
                <Area type="monotone" dataKey="alerts" stroke="#F59E0B" strokeWidth={1.5} fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm" data-testid="health-overview-card">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">System Health Overview</div>
          <div className="mt-4 space-y-3">
            <HealthBar label="Healthy" count={bands.Healthy} total={totalDevices} color="bg-emerald-500" />
            <HealthBar label="Watchlist" count={bands.Watchlist} total={totalDevices} color="bg-blue-500" />
            <HealthBar label="Needs Maintenance" count={bands.Maintenance} total={totalDevices} color="bg-amber-500" />
            <HealthBar label="Critical" count={bands.Critical} total={totalDevices} color="bg-red-500" />
            <HealthBar label="Offline" count={bands.Offline} total={totalDevices} color="bg-slate-400" />
          </div>
          <Button variant="outline" className="w-full mt-5 h-9" onClick={() => navigate('/devices')} data-testid="health-view-devices">
            View Device Monitor <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Card>
      </div>

      {/* Recent alerts + technician workload */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 border-slate-200 shadow-sm overflow-hidden" data-testid="recent-alerts-card">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Recent Alerts</div>
              <div className="mt-0.5 text-lg font-semibold text-slate-900">Latest diagnostic signals</div>
            </div>
            <Button variant="ghost" className="h-8 text-sm" onClick={() => navigate('/alerts')}>See all</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider">Time</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Customer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Device</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Alert Type</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Severity</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Suggested Action</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentAlerts.map((a) => (
                <TableRow
                  key={a.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate('/alerts')}
                  data-testid={`dashboard-alert-row-${a.id}`}
                >
                  <TableCell className="font-mono text-xs text-slate-500">{a.createdAgo}</TableCell>
                  <TableCell className="font-medium text-slate-900">{a.customer}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{a.deviceModel}</TableCell>
                  <TableCell className="text-sm">{a.type}</TableCell>
                  <TableCell><StatusBadge status={a.severity} /></TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-[240px] truncate">{a.recommended}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm" data-testid="tech-workload-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Technician Workload</div>
              <div className="mt-0.5 text-lg font-semibold text-slate-900">Active jobs per tech</div>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={techWorkload} layout="vertical" margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} width={70} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Bar dataKey="jobs" fill="#0052CC" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <Button variant="outline" className="w-full mt-4 h-9" onClick={() => navigate('/technicians')}>
            View Technicians <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Card>
      </div>

      {/* Revenue opportunities */}
      <Card className="p-6 border-slate-200 shadow-sm" data-testid="revenue-opps-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Revenue Opportunities</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-900">Customers who may need service soon</div>
          </div>
          <div className="font-mono text-sm text-slate-500">
            Est. ${revenueOpps.reduce((s, r) => s + r.revenue, 0).toLocaleString()} pipeline
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
          {revenueOpps.map((r) => {
            const Icon = r.icon;
            const accent = {
              blue: 'bg-blue-50 text-blue-700',
              emerald: 'bg-emerald-50 text-emerald-700',
              indigo: 'bg-indigo-50 text-indigo-700',
              violet: 'bg-violet-50 text-violet-700',
              amber: 'bg-amber-50 text-amber-800',
            }[r.accent];
            return (
              <div key={r.label} className="rounded-lg border border-slate-200 p-4 hover:border-slate-300 transition-colors">
                <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="mt-3 text-sm font-medium text-slate-900">{r.label}</div>
                <div className="mt-1 font-mono text-xs text-slate-500">{r.count} customers</div>
                <div className="mt-2 font-mono text-lg font-semibold text-slate-900">${r.revenue.toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function HealthBar({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-700 font-medium">{label}</span>
        <span className="font-mono text-slate-600">{count} <span className="text-slate-400">· {pct}%</span></span>
      </div>
      <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
